import { useState, useEffect, useMemo } from 'react';
import { Theme, themeColors } from './colors';
import { layoutStyles } from '../styles/layout.styles';
import { inputStyles } from '../styles/input.styles';

export const useTheme = (isMobile: boolean, showRightPanel: boolean) => {
  // Stored preference wins; system prefers-color-scheme is only the fallback.
  const [theme, setTheme] = useState<Theme>(() => {
    const stored = localStorage.getItem('theme');
    if (stored === 'light' || stored === 'dark') return stored;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });
  // Whether a stored preference existed at mount - if so, later system-theme
  // changes must not override the user's explicit choice.
  const hadStoredPref = useState<boolean>(() => {
    const stored = localStorage.getItem('theme');
    return stored === 'light' || stored === 'dark';
  })[0];

  const currentThemeColors = themeColors[theme];

  // Listen for system theme changes (only when the user never chose manually)
  useEffect(() => {
    if (hadStoredPref) return;
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      setTheme(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [hadStoredPref]);

  // Save theme preference
  useEffect(() => {
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Dynamic message action styles
  const messageActionStyles = useMemo(() => `
    .message-action-btn {
      background: none;
      border: none;
      padding: 5px 7px;
      border-radius: 8px;
      cursor: pointer;
      color: ${currentThemeColors.secondaryText};
      display: flex;
      align-items: center;
      opacity: 0.45;
      transition: background-color 0.15s ease, opacity 0.15s ease, color 0.15s ease;
      float: left;
      text-align: left;
    }

    .message-action-btn:hover {
      background-color: ${currentThemeColors.surface2};
      color: ${currentThemeColors.text};
      opacity: 1;
    }
  `, [currentThemeColors.secondaryText, currentThemeColors.surface2, currentThemeColors.text]);

  // Inject message action styles
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = messageActionStyles;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, [messageActionStyles]);

  // Dynamic layout styles
  const updatedLayoutStyles = useMemo(() => ({
    wrapper: {
      display: 'flex',
      // MOBILE APP-SHELL (2026-08-04, the structural iOS-keyboard fix): the
      // shell is a fixed-height COLUMN bound to --app-h (set from
      // visualViewport.height in Chat.tsx). When the keyboard opens the shell
      // shrinks, so the static composer footer rises above the keyboard IN
      // LAYOUT - Safari then has nothing to pan and the top bar stays put.
      // Desktop keeps the original 100vh row.
      flexDirection: isMobile ? ('column' as const) : ('row' as const),
      height: isMobile ? 'var(--app-h, 100vh)' : '100vh',
      ...(isMobile
        ? {
            position: 'fixed' as const,
            top: 0,
            left: 0,
            right: 0,
            // Follow Safari's keyboard pan 1:1 (--app-y = visualViewport
            // offsetTop, set in Chat.tsx): the shell stays glued to the
            // visible area, so nothing ever appears to slide up. Side effect
            // by design: fixed descendants (drawer, modals, scroll button)
            // now anchor to the shell = the visible area, which is where
            // overlays belong anyway.
            transform: 'translateY(var(--app-y, 0px))',
            willChange: 'transform' as const,
          }
        : { position: 'relative' as const }),
      background: currentThemeColors.appBackground,
      color: currentThemeColors.text,
      paddingTop: isMobile ? 'env(safe-area-inset-top)' : 0,
      maxWidth: '100%',
      overflowX: 'hidden' as const,
      WebkitOverflowScrolling: 'touch' as const
    },
    sidebar: {
      ...layoutStyles.sidebar,
      backgroundColor: currentThemeColors.sidebarBackground,
      borderRight: `1px solid ${currentThemeColors.border}`,
    },
    mainContent: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column' as const,
      // In the mobile column shell height comes from flex; 100% here fought
      // the shrinking shell. minHeight 0 lets the inner scroller actually
      // scroll inside a flex column.
      height: isMobile ? 'auto' : '100%',
      minHeight: 0,
      overflow: 'hidden',
      position: 'relative' as const,
      marginRight: showRightPanel ? '320px' : '0',
      // Static mobile navbar sits in flow now; no clearance hack needed.
      paddingTop: 0,
      transition: 'margin-right 0.3s ease',
      maxWidth: '100vw',
      overflowX: 'hidden' as const,
      backgroundColor: 'transparent',
    },
    chatContainer: {
      flex: 1,
      minHeight: 0,
      overflowY: 'auto' as const,
      marginRight: 0,
      transition: 'margin-right 0.3s ease',
      // 50px cleared the desktop top bar; the mobile navbar is in flow now,
      // so only breathing room remains (the 70+50=120px dead band is gone).
      paddingTop: isMobile ? '12px' : '50px',
      WebkitOverflowScrolling: 'touch' as const,
      maxWidth: '100vw',
      overflowX: 'hidden' as const,
    },
    navbar: isMobile ? {
      // STATIC on mobile (2026-08-04): a fixed top bar is exactly what
      // Safari's keyboard pan shoves off-screen. As the shell column's first
      // row it can no longer move.
      width: '100%',
      zIndex: 10,
      padding: '0 16px',
      backgroundColor: theme === 'dark' ? 'rgba(10, 11, 13, 0.62)' : 'rgba(247, 248, 250, 0.66)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      borderBottom: `1px solid ${currentThemeColors.line}`,
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      height: '56px',
      flexShrink: 0,
    } : {
      position: 'sticky' as const,
      top: 0,
      zIndex: 10,
      padding: '0 22px',
      backgroundColor: theme === 'dark' ? 'rgba(10, 11, 13, 0.55)' : 'rgba(247, 248, 250, 0.6)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      borderBottom: `1px solid ${currentThemeColors.line}`,
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      width: '100%',
      marginRight: 0,
      transition: 'margin-right 0.3s ease',
      height: '50px',
      minHeight: '50px',
      flexShrink: 0,
    },
    inputArea: {
      borderTop: `1px solid ${currentThemeColors.border}`,
      padding: '20px',
      backgroundColor: currentThemeColors.inputBackground,
      marginRight: 0,
      transition: 'margin-right 0.3s ease',
    }
  }), [currentThemeColors, isMobile, showRightPanel, theme]);

  // Dynamic message styles
  const messageStyles = useMemo(() => ({
    user: {
      backgroundColor: 'transparent',
      borderBottom: 'none',
    },
    assistant: {
      backgroundColor: 'transparent',
      borderBottom: 'none',
    },
    content: {
      maxWidth: '100%',
      overflowX: 'auto' as const,
      WebkitOverflowScrolling: 'touch' as const,
      padding: '0 4px',
      '& pre': {
        maxWidth: isMobile ? '85vw' : '100%',
        overflowX: 'auto' as const,
      }
    }
  }), [currentThemeColors, isMobile]);

  // Dynamic input styles
  const updatedInputStyles = useMemo(() => ({
    ...inputStyles,
    backgroundColor: currentThemeColors.inputBackground,
    border: `1px solid ${currentThemeColors.border}`,
    color: currentThemeColors.text,
  }), [currentThemeColors]);


  return {
    theme,
    setTheme,
    themeColors: themeColors, // Full theme colors object for backward compatibility
    currentThemeColors, // Current theme colors for new usage
    updatedLayoutStyles,
    messageStyles,
    updatedInputStyles
  };
};